<template>
  <ul
    :class="{
      timeline: true,
      disabledComponent: project.cancelled || project.archived,
    }"
  >
    <template v-for="stage in model.tasks">
      <closed-timeline-list-item
        v-if="stage.status === 'completed'"
        :key="stage.id"
        :model="stage"
        :root="root"
        :parent="model"
        :project="project"
        :events="filterEventsForStage(stage)"
        :notes="notes"
        :stage="stage"
        @add="onNoteAdded"
        :disableButton="disableButton"
      />
      <timeline-list-item
        v-else
        :key="stage.id"
        :preTitle="
          isFirstPendingStage(stage) && root.is_linear ? 'Up next' : ''
        "
        :model="stage"
        :root="root"
        :parent="model"
        :project="project"
        :events="filterEventsForStage(stage)"
        :notes="notes"
        :isOpen="
          stage.status === 'in_progress' || stage.status === 'awaiting_feedback'
        "
        @revision_send_preview_click="onRevisionSendPreviewClick"
        @revision_send_message_click="onRevisionSendMessageClick"
        @stage_send_message_click="onStageSendMessageClick"
        @revision_client_approve_click="onRevisionClientApproveClick"
        @revision_client_reject_click="onRevisionClientRejectClick"
        @revision_reject_revision_click="onRevisionClientRejectClick"
        @revision_stage_approve_click="onApproveStageFromRevisionClick"
        @add="onNoteAdded"
        :disableButton="disableButton"
      >
        <div class="col-lg-12" v-if="stage.tasks && stage.tasks.length > 0">
          <timeline-list
            :model="stage"
            :root="root"
            :project="project"
            v-if="stage.status === 'in_progress'"
            @workflow_updated="onWorkflowUpdated"
            :toDisableButton="disableButton"
          />
        </div>
        <stage-action-buttons
          v-if="
            $auth.user.isAgent &&
            stage.type === 'Stage' &&
            stage.status === 'in_progress'
          "
          @on_stage_client_approve_click="onStageClientApproveClick"
          @on_stage_add_revision_click="onStageAddRevisionClick"
          @revision_stage_approve_click="onApproveStageFromRevisionClick"
          @open_deliverables_tab="(e) => $emit('goto_tab', 'deliverables', e)"
          @open_feedback_tab="(e) => $emit('goto_tab', 'feedback')"
          :stage="stage"
          :workflow="root"
          :project="project"
          :disableButton="disableButton"
        />
      </timeline-list-item>
    </template>
    <CompleteTimelineActionButtons
      v-if="$auth.user.isAgent"
      :surveysFeedback="surveysFeedback"
      @on_stage_client_approve_click="onStageClientApproveClick"
      @on_stage_add_revision_click="onStageAddRevisionClick"
      @revision_stage_approve_click="onApproveStageFromRevisionClick"
      @open_deliverables_tab="(e) => $emit('goto_tab', 'deliverables', e)"
      @open_feedback_tab="(e) => $emit('goto_tab', 'feedback')"
      :workflow="root"
      :project="project"
    />
  </ul>
</template>

<script>
import {
  sendClientRevisionMessage,
  sendRevisionPreview,
  sendStageMessage,
} from "@/apis/projects";
import {
  appendRevision,
  approveStage,
  rejectRevision,
  updateWorkflow,
} from "@/apis/workflows";
import StageActionButtons from "./StageActionButtons.vue";
import CompleteTimelineActionButtons from "./CompleteTimelineActionButtons.vue";
import TimelineListItem from "./TimelineListItem.vue";
import workflowMixin from "../../../mixins/workflow";
import ClosedTimelineListItem from "@/components/ui/Timeline/ClosedTimelineListItem.vue";
import eventBus, { channels } from "@/eventBus";

export default {
  name: "TimelineList",
  components: {
    ClosedTimelineListItem,
    TimelineListItem,
    StageActionButtons,
    CompleteTimelineActionButtons,
  },
  data() {
    return {
      tenantProfileSetting: {},
      disableButton: this.toDisableButton,
    };
  },
  mixins: [workflowMixin],
  props: {
    model: Object, // Stage or Workflow
    root: Object, // Workflow
    project: Object,
    events: {
      type: Array,
      default: () => [],
    },
    notes: {
      type: Array,
      default: () => [],
    },
    surveysFeedback: {
      type: Array,
      default: () => [],
    },
    toDisableButton: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onNoteAdded(newNote) {
      this.$emit("add", newNote);
    },
    filterEventsForStage(stage) {
      return this.events.filter((e) => {
        const revisionId = JSON.parse(e.event)?.revisionId;
        const stageId = JSON.parse(e.event)?.stageId;
        return (
          stage?.tasks?.some((t) => t.id === revisionId) || stage.id === stageId
        );
      });
    },
    isFirstPendingStage(stage) {
      // this.model is a Workflow
      for (let i = 0; i < this.model.tasks.length; i++) {
        const modelStage = this.model.tasks[i];
        if (!modelStage.status || modelStage.status === "pending") {
          return modelStage === stage;
        }
      }
      return false;
    },
    onRevisionSendPreviewClick: async function (event) {
      // Find the matching task (revision) by ID
      this.disableButton = true;
      let rev = this.model.tasks.find(
        (revision) => revision.id === event.event_id
      );
      if (rev) {
        try {
          let firstStageWithRevs = this.root.tasks.find(
            (stage) => stage.tasks && stage.tasks.length > 0
          );
          let isFirstEmail = firstStageWithRevs.id === rev.id; // Is first revision of first stage with revisions

          let promise = sendRevisionPreview(
            this.$route.params.projectId,
            event.workflowId,
            event.event_id, // Revision Id
            event.emailText,
            event.reviewLink,
            isFirstEmail,
            event.isReminder || false,
            event.updateStatus
          );
          this.notifySuccess(
            "Revision preview sent<br/>... Updating analytics"
          );
          await promise; // So we show the message while we send since we're not really interested in the result.

          // If already 'awaiting_feedback', there's no need to refresh the page or project stats
          if (rev.status !== "awaiting_feedback") {
            eventBus.$emit(channels.refreshProject);
            return;
          } else {
            // TODO : Close the message textbox again after sending reminder
          }
          // this.$emit("workflow_updated", { revision: rev, stage: this.model }); // Send Revision and Stage
        } catch (err) {
          this.notifyError(err, "Error on sending Preview");
          return;
        } finally {
          this.disableButton = false;
        }

        if (this.$route.query?.action || this.$route.query?.revisionId) {
          await this.$router.replace({
            query: {
              ...this.$route.query,
              action: undefined,
              revisionId: undefined,
            },
          });
        }
        eventBus.$emit(channels.refreshProject);
      }
      this.disableButton = false;
    },
    onRevisionSendMessageClick: async function (event) {
      // Find the matching task (revision) by ID
      let rev = this.model.tasks.find(
        (revision) => revision.id === event.event_id
      );
      if (rev) {
        this.disableButton = true;
        try {
          let firstStageWithRevs = this.root.tasks.find(
            (stage) => stage.tasks && stage.tasks.length > 0
          );
          let isFirstEmail = firstStageWithRevs.id === rev.id; // Is first revision of first stage with revisions

          await sendClientRevisionMessage(
            this.project.id,
            this.root.id,
            event.event_id, // Revision Id
            event.emailText,
            isFirstEmail
          );
          this.notifySuccess("Stage message sent");
        } catch (err) {
          this.notifyError(err, "Error on sending message");
        } finally {
          this.disableButton = false;
        }
      }
    },
    onStageSendMessageClick: async function (event) {
      this.disableButton = true;
      try {
        const isFirstEmail = true;

        await sendStageMessage(
          this.project.id,
          event.workflowId,
          event.stageId,
          event.emailText,
          isFirstEmail
        );

        if (event.updateStatus) {
          const stage = this.model.tasks.find(
            (stage) => stage.id === event.stageId
          );
          if (stage) {
            await approveStage(this.root, stage);
            eventBus.$emit(channels.refreshProject);
          }
        }
        this.notifySuccess("Message sent");
      } catch (err) {
        this.notifyError(err, "Error on sending message");
      } finally {
        this.disableButton = false;
      }
    },
    onStageAddRevisionClick: async function (event) {
      this.disableButton = true;
      // We assume the PM wants to add a revision to start working on it. So we add a new rev, reject the current one and move to the new one
      // Find the matching task (stage) by ID
      const workflow = this.root;
      let stage = workflow.tasks.find((stage) => stage.id === event.id);
      if (stage) {
        this.disableButton = true;
        let lastRevision = stage.tasks[stage.tasks.length - 1];
        try {
          let newRevision = await appendRevision(workflow, stage);
          await rejectRevision(workflow, stage, lastRevision);
          stage.tasks.push(newRevision);
          this.notifySuccess("Revision added");
        } catch (err) {
          this.disableButton = false;
          this.notifyError(err, "Error on adding revision");
          return;
        } finally {
          this.disableButton = false;
        }
        eventBus.$emit(channels.refreshProject);
      }
      this.disableButton = false;
    },
    onStageClientApproveClick: async function (event) {
      this.disableButton = true;
      // [{"id": "ead68406-e9e6-4ffb-886b-01862baafd7b", "type": "Stage", "tasks": [{"id": "d89bf266-24a8-407e-b53e-f06c101b1fc6", "type": "Revision", "title": "Revision Round 1 of 3", "status": "completed"}, {"id": "fabf6b11-2504-4799-a6dd-2b5565b26fbe", "type": "Revision", "title": "Revision Round 2 of 3", "status": "completed"}, {"id": "77486283-f36e-4ac2-b278-2a9d0672c6e4", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Script Writing"}, {"id": "3967bb0b-5c0d-4dcc-9176-9871acecd4bd", "type": "Stage", "tasks": [{"id": "9b63dbad-d0c2-47a6-a6df-212c307cadb7", "type": "Revision", "title": "Revision Round 1 of 3"}, {"id": "a619ce70-9092-4f83-a9c7-e61ff8f48cb1", "type": "Revision", "title": "Revision Round 2 of 3"}, {"id": "9d9e1029-4778-4d71-8f43-8cba3e51865a", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Storyboard"}, {"id": "9676d8f2-1ba2-40b0-9d0a-89f1f9ec58ff", "type": "Stage", "tasks": [{"id": "8b6ba962-f48d-4626-8e3c-002b4421d059", "type": "Revision", "title": "Revision Round 1 of 3"}, {"id": "92ba4cf2-ce18-4fa7-ba39-999b56791bb3", "type": "Revision", "title": "Revision Round 2 of 3"}, {"id": "53dce7d2-cecb-4cf1-8755-995878799975", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Visual proposal"}, {"id": "74b011e5-df18-4e79-b70f-f2e60b00d07b", "type": "Stage", "tasks": [{"id": "7426fff0-8581-4a1e-bc2e-c6ab394b631b", "type": "Revision", "title": "Revision Round 1 of 3"}, {"id": "c057e6cb-8d4a-403e-b1d4-baf1115a0fd4", "type": "Revision", "title": "Revision Round 2 of 3"}, {"id": "d45f9dd8-4e1e-4de9-b885-3878850c3402", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Artwork creation"}, {"id": "5026ceaf-30f4-4533-b285-602a85df147b", "type": "Stage", "tasks": [{"id": "38abb649-492c-4b8e-8515-80382de30834", "type": "Revision", "title": "Revision Round 1 of 3"}, {"id": "04445243-c2a7-4039-a655-83b237874765", "type": "Revision", "title": "Revision Round 2 of 3"}, {"id": "a60222bb-aaf0-47f1-9b96-25e7b65a223a", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Animation"}, {"id": "fb2d9ef0-28f6-4b0d-9e1f-167137476e6e", "type": "Stage", "tasks": [{"id": "2369637f-b3fa-4098-8219-e178ae01ed30", "type": "Revision", "title": "Revision Round 1 of 3"}, {"id": "6c1e48b4-9ae9-4b44-8e21-eb0367b386c3", "type": "Revision", "title": "Revision Round 2 of 3"}, {"id": "1b8f9a52-5313-4e32-8e08-ca5bade07016", "type": "Revision", "title": "Revision Round 3 of 3"}], "title": "Delivery"}]
      // Find the matching task (stage) by ID
      let stage = this.model.tasks.find((stage) => stage.id === event.id);
      if (stage) {
        this.disableButton = true;
        try {
          await approveStage(this.root, stage);
          this.notifySuccess("Stage approved");
          // return this.onWorkflowUpdated(event);
        } catch (err) {
          this.disableButton = false;
          this.notifyError(err, "Error on approving stage");
          return;
        } finally {
          this.disableButton = false;
        }
        eventBus.$emit(channels.refreshProject);
      }
      this.disableButton = false;
    },
    onApproveStageFromRevisionClick: async function (event) {
      this.disableButton = true;
      if (event.type == "Stage") {
        try {
          await approveStage(this.root, event);
          this.notifySuccess("Stage approved");
          // return this.onWorkflowUpdated(event);
        } catch (err) {
          this.notifyError(err, "Error on approving stage");
        } finally {
          this.disableButton = false;
        }
        eventBus.$emit(channels.refreshProject);
      } else {
        console.log("wrong method called");
      }
      this.disableButton = false;
    },
    onRevisionClientApproveClick: async function () {
      // {"id": "f27f40a0-a5b7-4e75-8054-897e5dc4998a","type": "Revision","title": "Revision round 2 of 3","status": "in_progress"}
      this.disableButton = true;
      try {
        await approveStage(this.root, this.model);
        this.notifySuccess("Revision approved");
        // return this.onWorkflowUpdated(event);
      } catch (err) {
        this.notifyError(err, "Error on approving revision");
        return;
      } finally {
        this.disableButton = false;
      }
      eventBus.$emit(channels.refreshProject);
    },
    onRevisionClientRejectClick: async function (event) {
      // {"id": "f27f40a0-a5b7-4e75-8054-897e5dc4998a","type": "Revision","title": "Revision round 2 of 3","status": "in_progress"}
      this.disableButton = true;
      try {
        await rejectRevision(this.root, this.model, event);
        this.notifySuccess("Revision rejected");
        // return this.onWorkflowUpdated(event);
      } catch (err) {
        this.notifyError(err, "Error on rejecting revision");
        return;
      } finally {
        this.disableButton = false;
      }
      eventBus.$emit(channels.refreshProject);
    },
    onWorkflowUpdated: async function (event) {
      this.disableButton = true;
      if (!this.model.type) {
        try {
          // If this is the workflow, not a subtask
          await updateWorkflow(this.model);
          this.notifySuccess("Workflow updated");
          eventBus.$emit(channels.refreshProject);
        } catch (err) {
          this.notifyError(err, "Error on updating workflow");
        } finally {
          this.disableButton = false;
        }
      } else {
        this.disableButton = false;
        this.$emit("workflow_updated", { sub_event: event, stage: this.model }); // Send current Stage and triggering event
      }
    },
  },
};
</script>
